import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslate, useGetIdentity } from 'react-admin';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Divider,
  Avatar,
  Chip,
  IconButton,
  Grid,
  Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { Conversation, Ticket } from '../../../types/chat.types';
import { TicketStatus, TicketPriority } from '../../../types/chat.types';
import { useContactAvatarUrl } from '../../../hooks/useContactAvatarUrl';
import { useDateFormat } from '../../../utils/dateFormat';
import { formatPhoneForDisplay } from '../../../utils/phone';
import { TicketTagSelector } from '../../../components/tag/TicketTagSelector';
import { ConversationTagSelector } from '../../../components/tag/ConversationTagSelector';

interface DetailsPanelProps {
  /** Conversation to display details for */
  conversation: Conversation | null;
  /** Selected ticket (optional - if not provided, uses first open ticket from conversation) */
  ticket?: Ticket | null;
  /** Callback to close the panel */
  onClose?: () => void;
  /** Whether to show the close button */
  showCloseButton?: boolean;
  /** Display variant: 'drawer' (overlay) or 'inline' (side panel) */
  variant?: 'drawer' | 'inline';
  /** Width for inline variant */
  width?: number;
  /** Context: 'ticket' shows ticket info, 'conversation' shows conversation info with conversation tags */
  context?: 'ticket' | 'conversation';
}

export function DetailsPanel({
  conversation,
  ticket: selectedTicket,
  onClose,
  showCloseButton = false,
  variant = 'drawer',
  width,
  context = 'ticket',
}: DetailsPanelProps) {
  const translate = useTranslate();
  const { data: identity } = useGetIdentity();
  const currentUserId = identity?.id ? Number(identity.id) : 0;
  const { formatDateTime } = useDateFormat();

  // Collapsible section states
  const [contactExpanded, setContactExpanded] = useState(true);
  const [ticketExpanded, setTicketExpanded] = useState(true);

  // Tag state - initialized from ticket, updated locally on change
  const [ticketTags, setTicketTags] = useState<Array<{ id: number; name: string; color?: string | null }>>([]);
  const lastTagTicketIdRef = useRef<number | null>(null);

  // Resolve the ticket to read tags from
  const openTicketForTags = selectedTicket || conversation?.tickets?.find(
    (t) => t.status === 'open' || t.status === 'resolved'
  );

  // Sync tags from ticket when the ticket changes
  useEffect(() => {
    const ticketId = openTicketForTags?.id ?? null;
    if (ticketId === lastTagTicketIdRef.current) return;
    lastTagTicketIdRef.current = ticketId;

    if (openTicketForTags?.tags) {
      const mapped = openTicketForTags.tags.map((tt) =>
        tt.tag
          ? { id: tt.tag.id, name: tt.tag.name, color: tt.tag.color }
          : { id: tt.id ?? tt.tagId, name: tt.name ?? '', color: tt.color ?? null }
      );
      setTicketTags(mapped);
    } else {
      setTicketTags([]);
    }
  }, [openTicketForTags?.id, openTicketForTags?.tags]);

  const handleTagsChange = useCallback((newTags: Array<{ id: number; name: string; color?: string | null }>) => {
    setTicketTags(newTags);
  }, []);

  // Conversation tag state (for context === 'conversation')
  const [conversationTags, setConversationTags] = useState<Array<{ id: number; name: string; color?: string | null }>>([]);
  const lastTagConversationIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (context !== 'conversation') return;
    const convId = conversation?.id ? Number(conversation.id) : null;
    if (convId === lastTagConversationIdRef.current) return;
    lastTagConversationIdRef.current = convId;

    if (conversation?.tags) {
      const mapped = conversation.tags.map((ct) =>
        ct.tag
          ? { id: ct.tag.id, name: ct.tag.name, color: ct.tag.color }
          : { id: ct.tagId, name: '', color: null }
      );
      setConversationTags(mapped);
    } else {
      setConversationTags([]);
    }
  }, [context, conversation?.id, conversation?.tags]);

  const handleConversationTagsChange = useCallback((newTags: Array<{ id: number; name: string; color?: string | null }>) => {
    setConversationTags(newTags);
  }, []);

  // Get contact info (hooks must be called before any early returns)
  const contact = conversation?.contact;
  const resolvedAvatarUrl = useContactAvatarUrl(contact?.avatarFileId);

  // Don't render if no conversation
  if (!conversation) {
    return null;
  }

  // For internal DMs, get the other participant info instead of contact
  const otherParticipant = conversation.participants?.find(
    (p) => p.user && p.user.id !== currentUserId && p.user.name
  );

  // Determine what info to display: internal DM participant or external contact
  const displayInfo = otherParticipant?.user || conversation.contact;
  const isInternalDM = Boolean(otherParticipant);

  const contactAvatarUrl = !isInternalDM
    ? (contact?.profilePicUrl || resolvedAvatarUrl)
    : null;

  // Use selected ticket if provided, otherwise find first open/resolved ticket from conversation
  const openTicket = selectedTicket || conversation.tickets?.find(
    (ticket) => ticket.status === TicketStatus.OPEN || ticket.status === TicketStatus.RESOLVED
  );

  const statusColors: Record<TicketStatus, 'success' | 'warning' | 'default'> = {
    [TicketStatus.OPEN]: 'warning',
    [TicketStatus.RESOLVED]: 'success',
    [TicketStatus.CLOSED]: 'default',
    [TicketStatus.PENDING]: 'default',
  };

  const priorityColors: Record<TicketPriority, 'success' | 'info' | 'warning' | 'error'> = {
    [TicketPriority.LOW]: 'success',
    [TicketPriority.MEDIUM]: 'info',
    [TicketPriority.HIGH]: 'warning',
    [TicketPriority.URGENT]: 'error',
  };

  const isInline = variant === 'inline';

  return (
    <Paper
      variant={isInline ? 'outlined' : undefined}
      elevation={isInline ? 0 : undefined}
      sx={{
        width: width || '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: isInline ? undefined : 'none',
        borderRadius: 0,
        borderTop: 0,
        borderBottom: 0,
        borderRight: 0,
      }}
    >
      {/* Header with close button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          minHeight: 52,
        }}
      >
        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
          {translate('resources.conversations.details.title')}
        </Typography>
        {showCloseButton && (
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {/* Contact/User Information Section */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            {/* Collapsible Header */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ cursor: 'pointer' }}
              onClick={() => setContactExpanded(!contactExpanded)}
            >
              <Typography variant="subtitle2" color="textSecondary">
                {isInternalDM
                  ? translate('resources.conversations.details.userInfo')
                  : translate('resources.conversations.details.contactInfo')}
              </Typography>
              <IconButton size="small">
                {contactExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Box>

            <Collapse in={contactExpanded}>
              <Divider sx={{ my: 1 }} />

              <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                <Avatar
                  src={contactAvatarUrl ?? undefined}
                  sx={{ width: 48, height: 48 }}
                >
                  {displayInfo?.name ? (
                    displayInfo.name[0].toUpperCase()
                  ) : (
                    <PersonIcon />
                  )}
                </Avatar>
                <Box minWidth={0} flex={1}>
                  <Typography variant="body1" fontWeight={600} noWrap>
                    {displayInfo?.name || translate('resources.chat.unknownContact')}
                  </Typography>
                  {displayInfo?.email && (
                    <Typography variant="body2" color="textSecondary" noWrap>
                      {displayInfo.email}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Phone & Email contact info */}
              {!isInternalDM && contact && (contact.phoneNumber || contact.email) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
                  {contact.phoneNumber && (() => {
                    const formatted = formatPhoneForDisplay(contact.phoneNumber);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Box minWidth={0}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                            {formatted.national}
                          </Typography>
                          {formatted.international !== formatted.national && (
                            <Typography variant="caption" color="textSecondary" noWrap>
                              {formatted.international}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })()}
                  {contact.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                        {contact.email}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Additional fields */}
              {displayInfo && (
                <Grid container spacing={1}>
                  {isInternalDM ? (
                    <Grid size={12}>
                      <Typography variant="caption" color="textSecondary">
                        {translate('resources.user.fields.id')}
                      </Typography>
                      <Typography variant="body2">
                        {(displayInfo as any).id}
                      </Typography>
                    </Grid>
                  ) : (
                    <Grid size={12}>
                      <Typography variant="caption" color="textSecondary">
                        {translate('resources.contact.fields.uuid')}
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all', fontSize: '0.75rem' }}>
                        {(displayInfo as any).uuid}
                      </Typography>
                    </Grid>
                  )}

                  {!isInternalDM && (displayInfo as any).lastActivityAt && (
                    <Grid size={12}>
                      <Typography variant="caption" color="textSecondary">
                        {translate('resources.contact.fields.lastActivityAt')}
                      </Typography>
                      <Typography variant="body2">
                        {formatDateTime((displayInfo as any).lastActivityAt)}
                      </Typography>
                    </Grid>
                  )}

                  {!isInternalDM && (displayInfo as any).blocked && (
                    <Grid size={12}>
                      <Chip label="Blocked" size="small" color="error" />
                    </Grid>
                  )}
                </Grid>
              )}
            </Collapse>
          </CardContent>
        </Card>

        {/* Info Section: Ticket (tickets page) or Conversation (conversations page) */}
        <Card variant="outlined">
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            {/* Collapsible Header */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ cursor: 'pointer' }}
              onClick={() => setTicketExpanded(!ticketExpanded)}
            >
              <Typography variant="subtitle2" color="textSecondary">
                {context === 'conversation'
                  ? translate('resources.conversations.details.conversationInfo')
                  : translate('resources.conversations.details.ticketInfo')}
              </Typography>
              <IconButton size="small">
                {ticketExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Box>

            <Collapse in={ticketExpanded}>
              <Divider sx={{ my: 1 }} />

              {context === 'conversation' ? (
                /* Conversation details section */
                <Grid container spacing={1}>
                  <Grid size={12}>
                    <Typography variant="caption" color="textSecondary">
                      {translate('resources.tag.label')}
                    </Typography>
                    <Box mt={0.5}>
                      <ConversationTagSelector
                        conversationId={Number(conversation.id)}
                        tags={conversationTags}
                        onTagsChange={handleConversationTagsChange}
                      />
                    </Box>
                  </Grid>

                  {conversation.source && (
                    <Grid size={6}>
                      <Typography variant="caption" color="textSecondary">
                        {translate('resources.conversation.source')}
                      </Typography>
                      <Typography variant="body2">
                        {translate(`resources.conversation.sources.${conversation.source}`)}
                      </Typography>
                    </Grid>
                  )}

                  <Grid size={6}>
                    <Typography variant="caption" color="textSecondary">
                      {translate('resources.conversation.createdAt')}
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(conversation.createdAt)}
                    </Typography>
                  </Grid>

                  {conversation.lastActivityAt && (
                    <Grid size={6}>
                      <Typography variant="caption" color="textSecondary">
                        {translate('resources.conversation.fields.lastActivityAt')}
                      </Typography>
                      <Typography variant="body2">
                        {formatDateTime(conversation.lastActivityAt)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              ) : openTicket ? (
                /* Ticket details section */
                <>
                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    <ConfirmationNumberIcon fontSize="small" color="primary" />
                    <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                      #{openTicket.id}
                    </Typography>
                  </Box>

                  <Grid container spacing={1}>
                    <Grid size={6}>
                      <Typography variant="caption" color="textSecondary">
                        {translate('resources.ticket.fields.status')}
                      </Typography>
                      <Box mt={0.5}>
                        <Chip
                          label={translate(`resources.ticket.status.${openTicket.status}`)}
                          size="small"
                          color={statusColors[openTicket.status]}
                        />
                      </Box>
                    </Grid>

                    {openTicket.priority && (
                      <Grid size={6}>
                        <Typography variant="caption" color="textSecondary">
                          {translate('resources.ticket.fields.priority')}
                        </Typography>
                        <Box mt={0.5}>
                          <Chip
                            label={translate(`resources.ticket.priority.${openTicket.priority}`)}
                            size="small"
                            color={priorityColors[openTicket.priority]}
                            variant="outlined"
                          />
                        </Box>
                      </Grid>
                    )}

                    {openTicket.assignedUser && (
                      <Grid size={12}>
                        <Typography variant="caption" color="textSecondary">
                          {translate('resources.ticket.fields.assignedUser')}
                        </Typography>
                        <Typography variant="body2">
                          {openTicket.assignedUser.name || openTicket.assignedUser.email}
                        </Typography>
                      </Grid>
                    )}

                    {openTicket.ticketQueue && (
                      <Grid size={12}>
                        <Typography variant="caption" color="textSecondary">
                          {translate('resources.ticket.fields.ticketQueue')}
                        </Typography>
                        <Typography variant="body2">{openTicket.ticketQueue.name}</Typography>
                      </Grid>
                    )}

                    <Grid size={12}>
                      <Typography variant="caption" color="textSecondary">
                        {translate('resources.tag.label')}
                      </Typography>
                      <Box mt={0.5}>
                        <TicketTagSelector
                          ticketId={openTicket.id}
                          tags={ticketTags}
                          onTagsChange={handleTagsChange}
                        />
                      </Box>
                    </Grid>

                    <Grid size={12}>
                      <Typography variant="caption" color="textSecondary">
                        {translate('resources.ticket.fields.createdAt')}
                      </Typography>
                      <Typography variant="body2">
                        {formatDateTime(openTicket.createdAt)}
                      </Typography>
                    </Grid>
                  </Grid>
                </>
              ) : (
                <Typography variant="body2" color="textSecondary" align="center" py={2}>
                  {translate('resources.conversations.details.noTicket')}
                </Typography>
              )}
            </Collapse>
          </CardContent>
        </Card>
      </Box>
    </Paper>
  );
}
